// QMessageBox-style dialogs and a QMenu-style context menu.
import { $, escapeHtml } from "./util.js";

const ICON_GLYPH = { question: "?", information: "i", warning: "!", critical: "×" };
let queue = Promise.resolve();

// ----- stacked modals ------------------------------------------------------
// Bootstrap 5 gives every modal the same z-index, so a dialog opened from
// another dialog (Save As prompt, delete confirm, texture options...) can end
// up underneath it, and the lower dialog's focus trap steals focus from it.
// Each new dialog gets its own layer, and only the top dialog's backdrop is
// drawn: the dialogs underneath sit behind it, dimmed like the page.
const MODAL_Z = 1055;
const stack = [];
const backdrops = new Map(); // modal -> its backdrop element

function showTopBackdrop() {
  const top = stack[stack.length - 1];
  for (const [modal, el] of backdrops) el.classList.toggle("backdrop-under", modal !== top);
}

document.addEventListener("show.bs.modal", (e) => {
  const el = e.target;
  const below = stack[stack.length - 1];
  if (below && below !== el) bootstrap.Modal.getInstance(below)?._focustrap?.deactivate();
  if (!stack.includes(el)) stack.push(el);
  const z = MODAL_Z + (stack.length - 1) * 20;
  el.style.zIndex = String(z);
  // The backdrop for this modal is appended right after the event.
  setTimeout(() => {
    const all = document.querySelectorAll(".modal-backdrop:not([data-stacked])");
    const mine = all[all.length - 1];
    if (mine) {
      mine.style.zIndex = String(z - 5);
      mine.dataset.stacked = "1";
      backdrops.set(el, mine);
    }
    showTopBackdrop();
  }, 0);
});

// Hand the dim back to the dialog below while this one fades out.
document.addEventListener("hide.bs.modal", (e) => {
  if (e.defaultPrevented) return;
  const i = stack.indexOf(e.target);
  if (i < 0) return;
  stack.splice(i, 1);
  showTopBackdrop();
  stack.splice(i, 0, e.target);
  backdrops.get(e.target)?.classList.remove("backdrop-under");
});

document.addEventListener("hidden.bs.modal", (e) => {
  const i = stack.indexOf(e.target);
  if (i >= 0) stack.splice(i, 1);
  backdrops.delete(e.target);
  e.target.style.zIndex = "";
  showTopBackdrop();
  const top = stack[stack.length - 1];
  if (top) {
    // Bootstrap removes body.modal-open when any modal closes.
    document.body.classList.add("modal-open");
    const modal = bootstrap.Modal.getInstance(top);
    modal?._focustrap?.activate();
  }
});

/**
 * Show a message box. Resolves with the id of the clicked button
 * ("close" for the X / Escape).
 * buttons: [{ id, label, primary?, flat?, left? }]
 */
export function messageBox({ title = "ABVME", text = "", html = null, icon = "information", buttons = [{ id: "ok", label: "OK", primary: true }], details = "" }) {
  const run = () =>
    new Promise((resolve) => {
      const el = $("#msgBox");
      el.querySelector(".title-text").textContent = title;
      const iconEl = el.querySelector(".msgbox-icon");
      iconEl.className = `msgbox-icon ${icon}`;
      iconEl.textContent = ICON_GLYPH[icon] || "i";
      const textEl = el.querySelector(".msgbox-text");
      textEl.innerHTML = html ?? escapeHtml(text);
      if (details) textEl.insertAdjacentHTML("beforeend", `<div class="msgbox-details">${escapeHtml(details)}</div>`);
      const footer = el.querySelector(".modal-footer");
      footer.innerHTML = "";
      let hasLeft = false;
      for (const b of buttons) {
        const btn = document.createElement("button");
        btn.className = b.flat ? "btn-flat" : `btn btn-abv${b.primary ? " btn-primary-abv" : ""}`;
        btn.textContent = b.label;
        btn.dataset.result = b.id;
        if (b.left) {
          btn.classList.add("me-auto");
          footer.prepend(btn);
          hasLeft = true;
        } else footer.appendChild(btn);
      }
      footer.style.justifyContent = hasLeft ? "flex-start" : "flex-end";
      const modal = bootstrap.Modal.getOrCreateInstance(el, { keyboard: true });
      let result = "close";
      const onClick = (e) => {
        const r = e.target.closest("[data-result]");
        if (!r) return;
        result = r.dataset.result;
        modal.hide();
      };
      el.addEventListener("click", onClick);
      el.addEventListener(
        "shown.bs.modal",
        () => (footer.querySelector(".btn-primary-abv") || footer.querySelector("button"))?.focus(),
        { once: true },
      );
      el.addEventListener(
        "hidden.bs.modal",
        () => {
          el.removeEventListener("click", onClick);
          resolve(result);
        },
        { once: true },
      );
      modal.show();
    });
  const p = queue.then(run);
  queue = p.catch(() => {});
  return p;
}

export async function askYesNo(title, text) {
  const r = await messageBox({
    title,
    text,
    icon: "question",
    buttons: [
      { id: "yes", label: "Yes", primary: true },
      { id: "no", label: "No" },
    ],
  });
  return r === "yes";
}

export const showInfo = (title, text, details) => messageBox({ title, text, details, icon: "information" });
export const showWarning = (title, text, details) => messageBox({ title, text, details, icon: "warning" });
export const showError = (title, text, details) => messageBox({ title, text, details, icon: "critical" });

/** Ask for one line of text. Resolves with the text, or null when cancelled. */
export async function promptText(title, label, value = "", { password = false, okLabel = "OK" } = {}) {
  const id = `prompt-${Math.random().toString(36).slice(2)}`;
  let captured = null;
  const watcher = (e) => {
    if (e.target.closest?.("[data-result]")) captured = document.getElementById(id)?.value ?? null;
  };
  document.addEventListener("click", watcher, true);
  const keyHandler = (e) => {
    if (e.key === "Enter" && e.target.id === id) {
      e.preventDefault();
      captured = e.target.value;
      $("#msgBox").querySelector('[data-result="ok"]')?.click();
    }
  };
  document.addEventListener("keydown", keyHandler, true);
  const shown = () => setTimeout(() => {
    const input = document.getElementById(id);
    input?.focus();
    input?.select();
  }, 50);
  $("#msgBox").addEventListener("shown.bs.modal", shown, { once: true });
  const result = await messageBox({
    title,
    icon: "question",
    html: `<label class="form-label" for="${id}">${escapeHtml(label)}</label>
           <input class="form-control" id="${id}" type="${password ? "password" : "text"}" value="${escapeHtml(value)}" spellcheck="false" autocomplete="off" />`,
    buttons: [
      { id: "ok", label: okLabel, primary: true },
      { id: "cancel", label: "Cancel" },
    ],
  });
  document.removeEventListener("click", watcher, true);
  document.removeEventListener("keydown", keyHandler, true);
  return result === "ok" ? captured : null;
}

/** Wrap a Bootstrap modal element so it resolves when hidden. */
export function openModal(el, { onShown } = {}) {
  return new Promise((resolve) => {
    const modal = bootstrap.Modal.getOrCreateInstance(el);
    if (onShown) el.addEventListener("shown.bs.modal", onShown, { once: true });
    el.addEventListener("hidden.bs.modal", () => resolve(), { once: true });
    modal.show();
  });
}

export function closeModal(el) {
  bootstrap.Modal.getInstance(el)?.hide();
}

// A dialog that closes with the focus inside it (its own button, a list in it)
// would be hidden from assistive tech while focused: browsers warn ("Blocked
// aria-hidden on an element because its descendant retained focus"). Let go of
// the focus first (it goes back to the page).
document.addEventListener("hide.bs.modal", (e) => {
  if (e.target.contains(document.activeElement)) document.activeElement.blur();
});

// ----- context menu -------------------------------------------------------

let ctx = null;
function closeCtx() {
  if (!ctx) return;
  const { el, anchor } = ctx;
  ctx = null;
  // Keep keyboard focus in place (e.g. inside a dialog) when the menu had it.
  if (anchor && el.contains(document.activeElement)) anchor.focus();
  el.remove();
  anchor?.setAttribute("aria-expanded", "false");
  document.removeEventListener("pointerdown", ctxOutside, true);
  window.removeEventListener("keydown", ctxKey, true);
  document.removeEventListener("scroll", ctxScroll, true);
  window.removeEventListener("resize", closeCtx);
}
// Close when the page under the menu moves (not on stray scroll events).
function ctxScroll(e) {
  if (!ctx || ctx.el.contains(e.target)) return;
  if (!ctx.anchor) return closeCtx();
  if (Math.abs(ctx.anchor.getBoundingClientRect().top - ctx.anchorTop) > 2) closeCtx();
}
function ctxOutside(e) {
  if (ctx && !ctx.el.contains(e.target) && !ctx.anchor?.contains(e.target)) closeCtx();
}
function ctxKey(e) {
  if (!ctx) return;
  if (e.key === "Escape") {
    // Close only the menu, not the dialog it was opened from.
    e.preventDefault();
    e.stopPropagation();
    closeCtx();
  } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
    e.preventDefault();
    e.stopPropagation();
    const items = [...ctx.el.querySelectorAll(".dropdown-item:not(.disabled)")];
    const i = items.indexOf(document.activeElement);
    const next = e.key === "ArrowDown" ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[next]?.focus();
  } else if (e.key === "Tab") closeCtx();
}

/**
 * Show a menu at (x, y), or under `anchor` (right-aligned with it) when given.
 * items: [{ label, icon?, disabled?, action }] or "-" for a separator.
 */
export function contextMenu(x, y, items, { anchor = null } = {}) {
  const toggling = anchor && ctx?.anchor === anchor;
  closeCtx();
  if (toggling) return; // a second click on the same button closes its menu
  const withIcons = items.some((item) => item.icon);
  const ul = document.createElement("ul");
  ul.className = "dropdown-menu qmenu ctx-menu show";
  ul.setAttribute("role", "menu");
  for (const item of items) {
    const li = document.createElement("li");
    if (item === "-") li.innerHTML = '<hr class="dropdown-divider" />';
    else {
      const ico = withIcons ? (item.icon ? `<img class="ico" src="/icons/${item.icon}.svg" alt="" />` : '<span class="ico-space"></span>') : "";
      li.innerHTML = `<button type="button" role="menuitem" class="dropdown-item${item.disabled ? " disabled" : ""}${item.danger ? " danger" : ""}">${ico}<span class="label">${escapeHtml(item.label)}</span></button>`;
      li.firstChild.addEventListener("click", () => {
        closeCtx();
        if (!item.disabled) item.action();
      });
    }
    ul.appendChild(li);
  }
  // Inside a dialog the menu must live in it, or the dialog's focus trap pulls
  // focus back out of the menu.
  (anchor?.closest(".modal") || document.body).appendChild(ul);
  if (anchor) {
    const r = anchor.getBoundingClientRect();
    x = r.right - ul.offsetWidth;
    y = r.bottom + 2;
    if (y + ul.offsetHeight > innerHeight - 4 && r.top - ul.offsetHeight - 2 >= 4) y = r.top - ul.offsetHeight - 2;
  }
  ul.style.left = `${Math.max(4, Math.min(x, innerWidth - ul.offsetWidth - 4))}px`;
  ul.style.top = `${Math.max(4, Math.min(y, innerHeight - ul.offsetHeight - 4))}px`;
  ctx = { el: ul, anchor, anchorTop: anchor?.getBoundingClientRect().top };
  if (anchor) {
    anchor.setAttribute("aria-expanded", "true");
    ul.querySelector(".dropdown-item:not(.disabled)")?.focus();
  }
  window.addEventListener("keydown", ctxKey, true); // before Bootstrap's dropdown keys (document, capture)
  document.addEventListener("scroll", ctxScroll, true);
  window.addEventListener("resize", closeCtx);
  setTimeout(() => document.addEventListener("pointerdown", ctxOutside, true), 0);
}
